import React, { useState } from 'react';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import notificationService from '../../../../../services/notificationService';
import { fundsAPI } from '../../../../../services/api/fundsAPI';
import { extractBalanceData } from '../../FundsManagement/components/utils';
import UserDetailModal from './UserDetailModal';

/* ==========================================================================
   Helpers
   ========================================================================== */

const getUserId = (user) => {
  if (!user) return null;
  if (user.id && typeof user.id === 'string' && user.id.trim() !== '') return user.id;
  if (user.accountId && typeof user.accountId === 'string' && user.accountId.trim() !== '') return user.accountId;
  if (user._id && typeof user._id === 'string' && user._id.trim() !== '') return user._id;
  if (user.email && typeof user.email === 'string' && user.email.trim() !== '') return user.email;
  if (user.walletAddress && typeof user.walletAddress === 'string' && user.walletAddress.trim() !== '') return user.walletAddress;
  const possibleIds = ['userId', 'user_id', 'uid', 'UUID', 'uuid', 'identifier', 'username'];
  for (const field of possibleIds) {
    if (user[field] && typeof user[field] === 'string' && user[field].trim() !== '') return user[field];
  }
  return null;
};

const getUserInitials = (name, username) => {
  if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return username ? username.slice(0, 2).toUpperCase() : 'U';
};

const formatDate = (dateString) => {
  return dateString ? new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }) : 'N/A';
};

const getUserTypeClass = (type) => {
  switch (type?.toLowerCase()) {
    case 'admin':   return 'type-admin';
    case 'manager': return 'type-manager';
    default:        return 'type-client';
  }
};

const getUserTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'admin':   return 'fa-shield-alt';
    case 'manager': return 'fa-user-tie';
    default:        return 'fa-user';
  }
};

/* ==========================================================================
   UserRow — individual table row with lazy balance
   ========================================================================== */

const UserRow = ({ user, onBlock, onUnblock, onViewDetails, isSelected }) => {
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const userId = getUserId(user);

  const fetchBalance = async (e) => {
    e.stopPropagation();
    if (!userId || loadingBalance) return;
    setLoadingBalance(true);
    try {
      const response = await fundsAPI.getUserBalance(userId);
      const balanceData = extractBalanceData(response);
      setBalance(balanceData?.balance || balanceData?.currentBalance || 0);
    } catch (error) {
      console.error(`Failed to fetch balance for user ${userId}:`, error);
      setBalance('Error');
      notificationService.error('Failed to fetch user balance');
    } finally {
      setLoadingBalance(false);
    }
  };

  const renderBalance = () => {
    if (balance !== null) {
      if (loadingBalance) {
        return (
          <div className="balance-cell balance-loading">
            <div className="spinner-mini"></div>
            <span>Loading...</span>
          </div>
        );
      }
      if (balance === 'Error') {
        return (
          <div className="balance-cell balance-error">
            <i className="fas fa-exclamation-circle"></i>
            <span>Error</span>
          </div>
        );
      }
      return (
        <span className="balance-cell balance-value">
          ${parseFloat(balance || 0).toFixed(2)}
        </span>
      );
    }
    return (
      <button className="btn-show-balance" onClick={fetchBalance}>
        <i className="fas fa-wallet"></i>
        Show Balance
      </button>
    );
  };

  return (
    <tr
      className={isSelected ? 'selected' : ''}
      onClick={() => onViewDetails(user)}
      style={{ cursor: 'pointer' }}
    >
      {/* User Info */}
      <td>
        <div className="user-cell">
          <div className="user-avatar">
            {getUserInitials(user.name, user.username)}
          </div>
          <div className="user-meta">
            <h4>{user.name || user.username || userId || 'Unknown User'}</h4>
            <span>{user.email || user.walletAddress || user.username}</span>
          </div>
        </div>
      </td>

      {/* Status */}
      <td>
        <span className={`status-badge ${user.isBlocked ? 'blocked' : 'active'}`}>
          <span className={`status-dot ${user.isBlocked ? 'blocked' : 'active'}`}></span>
          {user.isBlocked ? 'Blocked' : 'Active'}
        </span>
      </td>

      {/* User Type */}
      <td>
        <div className="user-type-cell">
          <i className={`fas ${getUserTypeIcon(user.role || user.type)} ${getUserTypeClass(user.role || user.type)}`}></i>
          <span>{user.type || user.role || 'Client'}</span>
        </div>
      </td>

      {/* Balance */}
      <td>{renderBalance()}</td>

      {/* Registered */}
      <td>{formatDate(user.createdAt)}</td>

      {/* Actions */}
      <td>
        <div className="action-group">
          <button
            className="btn-icon"
            onClick={(e) => { e.stopPropagation(); onViewDetails(user); }}
            title="View Details"
            aria-label="View user details"
          >
            <i className="fas fa-eye"></i>
          </button>
          {user.isBlocked ? (
            <button
              className="btn-icon success"
              onClick={(e) => { e.stopPropagation(); onUnblock(user); }}
              title="Unblock User"
              aria-label="Unblock user"
            >
              <i className="fas fa-unlock"></i>
            </button>
          ) : (
            <button
              className="btn-icon danger"
              onClick={(e) => { e.stopPropagation(); onBlock(user); }}
              title="Block User"
              aria-label="Block user"
            >
              <i className="fas fa-ban"></i>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

/* ==========================================================================
   UserList — main table component
   ========================================================================== */

const UserList = ({
  users,
  loading,
  onBlockUser,
  onUnblockUser,
  onDeleteUser,
  selectedUser,
  onSelectUser,
  pagination,
  onPageChange
}) => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    isLoading: false
  });

  /* ---- Confirm dialog helpers ---- */
  const openConfirm = (title, message, onConfirmAction) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm: onConfirmAction, isLoading: false });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, isLoading: false });
  };

  const handleBlockClick = (user) => {
    const userId = getUserId(user);
    if (!userId) return notificationService.error('User ID is required');

    openConfirm(
      'Block User',
      <div>
        <p style={{ marginBottom: '12px' }}>
          Are you sure you want to block <strong>{user.name || user.username}</strong>?
        </p>
        <p style={{ color: '#64748b', fontSize: '13px' }}>
          This action will prevent the user from accessing their account and performing any trading activities.
        </p>
      </div>,
      async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        const success = await onBlockUser(userId);
        closeConfirmDialog();
        if (success && detailModalOpen) setDetailModalOpen(false);
      }
    );
  };

  const handleUnblockClick = (user) => {
    const userId = getUserId(user);
    if (!userId) return notificationService.error('User ID is required');

    openConfirm(
      'Unblock User',
      <div>
        <p style={{ marginBottom: '12px' }}>
          Are you sure you want to unblock <strong>{user.name || user.username}</strong>?
        </p>
        <p style={{ color: '#64748b', fontSize: '13px' }}>
          This action will restore the user's access to their account.
        </p>
      </div>,
      async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        const success = await onUnblockUser(userId);
        closeConfirmDialog();
        if (success && detailModalOpen) setDetailModalOpen(false);
      }
    );
  };

  /* ---- Detail modal ---- */
  const handleViewDetails = (user) => {
    setCurrentUser(user);
    setDetailModalOpen(true);
    onSelectUser(user);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setCurrentUser(null);
    onSelectUser(null);
  };

  /* ---- Pagination ---- */
  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;
    const { page, pages, total } = pagination;

    return (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          Showing <strong>{users.length}</strong> of <strong>{total}</strong> users
          {pages > 1 && ` (Page ${page} of ${pages})`}
        </div>
        <div className="pagination-nav">
          <button
            className="page-btn"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="page-btn active" aria-current="page">
            {page}
          </button>
          <button
            className="page-btn"
            onClick={() => onPageChange(page + 1)}
            disabled={page === pages}
            aria-label="Next page"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  /* ---- Skeleton Loading State ---- */
  if (loading && users.length === 0) {
    return (
      <div className="user-list-card">
        {/* Skeleton stats row — mimics the table header */}
        <div className="skeleton-table">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div className="skeleton-table-row" key={i}>
              <div className="skeleton skeleton-avatar"></div>
              <div className="skeleton-cell">
                <div className="skeleton skeleton-text"></div>
                <div className="skeleton skeleton-text short"></div>
              </div>
              <div className="skeleton skeleton-badge"></div>
              <div className="skeleton skeleton-badge" style={{ width: '50px' }}></div>
              <div className="skeleton skeleton-text xshort" style={{ marginBottom: 0 }}></div>
              <div className="skeleton skeleton-text xshort" style={{ marginBottom: 0 }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Empty State ---- */
  if (users.length === 0) {
    return (
      <div className="user-list-card">
        <div className="empty-state">
          <div className="empty-illustration">
            <i className="fas fa-users-slash"></i>
          </div>
          <h3>No users found</h3>
          <p>Try adjusting your search or filters to find what you're looking for.</p>
        </div>
      </div>
    );
  }

  /* ---- Main Table ---- */
  return (
    <div className="user-list-card">
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
        isLoading={confirmDialog.isLoading}
      />

      {/* Detail Modal */}
      <UserDetailModal
        isOpen={detailModalOpen}
        user={currentUser}
        onClose={closeDetailModal}
        onBlockUser={() => handleBlockClick(currentUser)}
        onUnblockUser={() => handleUnblockClick(currentUser)}
        onDeleteUser={onDeleteUser}
      />

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>User</th>
              <th style={{ width: '12%' }}>Status</th>
              <th style={{ width: '12%' }}>Type</th>
              <th style={{ width: '13%' }}>Balance</th>
              <th style={{ width: '15%' }}>Registered</th>
              <th style={{ width: '23%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow
                key={getUserId(user) || Math.random()}
                user={user}
                onBlock={handleBlockClick}
                onUnblock={handleUnblockClick}
                onViewDetails={handleViewDetails}
                isSelected={selectedUser && getUserId(selectedUser) === getUserId(user)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default UserList;
